import {Component, EventEmitter, OnDestroy, OnInit, Output} from '@angular/core';
import {
  ReactiveFormsModule,
  UntypedFormBuilder, UntypedFormControl,
  UntypedFormGroup,
  Validators
} from "@angular/forms";
import {BsModalRef} from 'ngx-bootstrap/modal';
import { BehaviorSubject, Observable, map, shareReplay, tap } from 'rxjs';
import {BsDatepickerModule} from "ngx-bootstrap/datepicker";
import {TimepickerModule} from "ngx-bootstrap/timepicker";
import {CommonModule, NgFor, NgIf} from "@angular/common";
import {TypeaheadMatch, TypeaheadModule} from "ngx-bootstrap/typeahead";
import {ShipmentPrefAddress, StateCountry} from "../../../shared/common-components/common-structure";
import {RatesSroMasterService} from "../../../services/rates-sro-master/rates-sro-master.service";
import {StateCountryService} from "../../../services/state-country/state-country.service";
import {CommonValidators} from "../../../shared/common-components/common-validators";
import {filter, takeWhile} from "rxjs/operators";
import {NameAndAddressService} from "../../../services/name-and-address/name-and-address.service";
import {TabsModule} from "ngx-bootstrap/tabs";


@Component({
  selector: 'app-frt-oto-rate-query-addresses',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    BsDatepickerModule,
    TimepickerModule,
    TypeaheadModule,
    TabsModule,
    CommonModule,
    NgFor, NgIf],
  templateUrl: './frt-oto-rate-query-addresses.component.html',
  styleUrl: './frt-oto-rate-query-addresses.component.css'
})
export class FrtOtoRateQueryAddressesComponent implements OnInit, OnDestroy {
  observer$ = new BehaviorSubject(true);
  addressForms: any;
  private emittedAddressData = false;
  isFormInvalidValue = [];
  public activeTab: string;
  addressOriginForm: UntypedFormGroup;
  addressShipperForm: UntypedFormGroup;
  addressBillToForm: UntypedFormGroup;
  addressDestinationForm: UntypedFormGroup;
  addressConsigneeForm: UntypedFormGroup;
  issuingOfficeForm: UntypedFormGroup;
  dataShipperSearchAddressResults$: Observable<any>;
  dataOriginSearchAddressResults$: Observable<any>;
  dataBillToSearchAddressResults$: Observable<any>;
  dataDestinationSearchAddressResults$: Observable<any>;
  dataConsigneeSearchAddressResults$: Observable<any>;
  @Output()
  emitData: EventEmitter<any> = new EventEmitter();
  private defaultCountry = new BehaviorSubject<StateCountry>(null);
  states;
  allCountriesFrtList$ = this.stateCountryService.getAllCountries().pipe(
    map(stateCountries => stateCountries.filter(country => {
      const businessType = String(country.businessType || '').toLowerCase();
      return (businessType.includes('frt') || businessType.includes('hhg')) && country.parentCountryId === 326;
    })),
    tap((allCountries) => {
      const defaultOrigin = allCountries.find(x => {
        return x.stateCountryCode === 'US00';
      });
      this.defaultCountry.next(defaultOrigin);
    }),
    shareReplay(1)
  );

  constructor(private ratesSroMasterService: RatesSroMasterService, private bsModalRef: BsModalRef,
              private formBuilder: UntypedFormBuilder, private nameAndAddressService: NameAndAddressService, private stateCountryService: StateCountryService) {
    this.dataOriginSearchAddressResults$ = this.createLocationSearch(() => this.addressOriginForm);
    this.dataShipperSearchAddressResults$ = this.createLocationSearch(() => this.addressShipperForm);
    this.dataBillToSearchAddressResults$ = this.createLocationSearch(() => this.addressBillToForm);
    this.dataDestinationSearchAddressResults$ = this.createLocationSearch(() => this.addressDestinationForm);
    this.dataConsigneeSearchAddressResults$ = this.createLocationSearch(() => this.addressConsigneeForm);
  }

  private createLocationSearch(form: () => UntypedFormGroup): Observable<any> {
    return new Observable(observer => {
      const searchString = form()?.get('searchString')?.value;
      if (!searchString) {
        observer.next([]);
        observer.complete();
        return;
      }
      this.ratesSroMasterService.getLocations(searchString).subscribe({
        next: result => observer.next(result),
        error: error => observer.error(error),
        complete: () => observer.complete()
      });
    });
  }

  typeaheadShipperOnSelect(e: TypeaheadMatch, recordType: string): void {
    const uspsCity = e.item;
    const state = this.states?.find(stateDetails => {
      return stateDetails.stateCountryCode2Digit === uspsCity.stateAbbrev;
    });
    switch (recordType) {
      case "origin":
        this.addressOriginForm.get('uspsCity').setValue(e.item);
        this.addressOriginForm.get('city').setValue(uspsCity.cityName);
        this.addressOriginForm.get('state').setValue(uspsCity.stateAbbrev);
        this.addressOriginForm.get('zipCode').setValue(uspsCity.zipCode);
        this.addressOriginForm.get('stateId').setValue(state ? state.stateCountryId : undefined);
        break;
      case "shipper":
        this.addressShipperForm.get('uspsCity').setValue(e.item);
        this.addressShipperForm.get('city').setValue(uspsCity.cityName);
        this.addressShipperForm.get('state').setValue(uspsCity.stateAbbrev);
        this.addressShipperForm.get('zipCode').setValue(uspsCity.zipCode);
        this.addressShipperForm.get('stateId').setValue(state ? state.stateCountryId : undefined);
        break;
      case "billTo":
        this.addressBillToForm.get('uspsCity').setValue(e.item);
        this.addressBillToForm.get('city').setValue(uspsCity.cityName);
        this.addressBillToForm.get('state').setValue(uspsCity.stateAbbrev);
        this.addressBillToForm.get('zipCode').setValue(uspsCity.zipCode);
        this.addressBillToForm.get('stateId').setValue(state ? state.stateCountryId : undefined);
        break;
      case "destination":
        this.addressDestinationForm.get('uspsCity').setValue(e.item);
        this.addressDestinationForm.get('city').setValue(uspsCity.cityName);
        this.addressDestinationForm.get('state').setValue(uspsCity.stateAbbrev);
        this.addressDestinationForm.get('zipCode').setValue(uspsCity.zipCode);
        this.addressDestinationForm.get('stateId').setValue(state ? state.stateCountryId : undefined);
        break;
      case "consignee":
        this.addressConsigneeForm.get('uspsCity').setValue(e.item);
        this.addressConsigneeForm.get('city').setValue(uspsCity.cityName);
        this.addressConsigneeForm.get('state').setValue(uspsCity.stateAbbrev);
        this.addressConsigneeForm.get('zipCode').setValue(uspsCity.zipCode);
        this.addressConsigneeForm.get('stateId').setValue(state ? state.stateCountryId : undefined);
        break;
      default:
        break;
    }
  }

  ngOnDestroy(): void {
    if (!this.emittedAddressData && this.addressOriginForm && this.addressDestinationForm) {
      this.emitAddressData();
    }
    this.observer$.next(false);
  }

  /**
   * @description initializes the form and gets all the accessorials
   */
  ngOnInit() {
    this.initializeForm();
    this.nameAndAddressService.allStates$.pipe(takeWhile(() => this.observer$.value)).subscribe(res => {
      this.states = res;
    });
    this.bindCountrySelection(this.addressOriginForm, true);
    this.bindCountrySelection(this.addressShipperForm, false);
    this.bindCountrySelection(this.addressBillToForm, false);
    this.bindCountrySelection(this.addressDestinationForm, true);
    this.bindCountrySelection(this.addressConsigneeForm, false);
    this.defaultCountry
      .pipe(filter(value => value != null))
      .pipe(takeWhile(() => this.observer$.value))
      .subscribe(defaultCountry => {
        const formEntries: Array<[string, UntypedFormGroup]> = [
          ['origin', this.addressOriginForm], ['shipper', this.addressShipperForm],
          ['billTo', this.addressBillToForm], ['destination', this.addressDestinationForm],
          ['consignee', this.addressConsigneeForm]
        ];
        formEntries.forEach(([name, form]) => {
          const savedForm = this.addressForms?.[name];
          form.get('countryId').setValue(savedForm?.countryId || defaultCountry.stateCountryId);
          if (savedForm) form.patchValue(savedForm, {emitEvent: false});
        });
        if (this.addressForms?.issuingOffice) {
          this.issuingOfficeForm.patchValue(this.addressForms.issuingOffice, {emitEvent: false});
        }
      });
  }

  private bindCountrySelection(form: UntypedFormGroup, isRequiredAddress: boolean): void {
    form.get('countryId').valueChanges.pipe(takeWhile(() => this.observer$.value)).subscribe(countryId => {
      const isUnitedStates = Number(countryId) === 326;
      const locationFields = ['city', 'state', 'zipCode'];
      locationFields.forEach(field => {
        const control = form.get(field);
        if (isUnitedStates) {
          control.disable({emitEvent: false});
        } else {
          control.enable({emitEvent: false});
        }
        control.reset('', {emitEvent: false});
      });
      const stateId = form.get('stateId');
      stateId.reset('', {emitEvent: false});
      stateId.disable({emitEvent: false});
      const selectedLocation = form.get('uspsCity');
      selectedLocation.reset('', {emitEvent: false});
      if (isUnitedStates && isRequiredAddress) {
        selectedLocation.setValidators(Validators.required);
        selectedLocation.enable({emitEvent: false});
      } else {
        selectedLocation.clearValidators();
        selectedLocation.disable({emitEvent: false});
      }
      selectedLocation.updateValueAndValidity({emitEvent: false});
      form.get('searchString').reset('', {emitEvent: false});
      if (isUnitedStates) {
        form.get('searchString').enable({emitEvent: false});
      } else {
        form.get('searchString').disable({emitEvent: false});
      }
    });
  }

  clearLocation(recordType: string): void {
    const form = this.getAddressForm(recordType);
    if (!form) return;
    ['city', 'state', 'stateId', 'zipCode', 'uspsCity'].forEach(field =>
      form.get(field).reset('', {emitEvent: false})
    );
  }

  private getAddressForm(recordType: string): UntypedFormGroup {
    switch (recordType) {
      case 'origin': return this.addressOriginForm;
      case 'shipper': return this.addressShipperForm;
      case 'billTo': return this.addressBillToForm;
      case 'destination': return this.addressDestinationForm;
      case 'consignee': return this.addressConsigneeForm;
      default: return null;
    }
  }



  /**
   * @description initializes frt query form.
   */
  initializeForm() {
    this.addressOriginForm = this.formBuilder.group({
      addressLine1: new UntypedFormControl('', [Validators.required]),
      addressLine2: new UntypedFormControl('', [Validators.maxLength(100), Validators.required]),
      searchString: new UntypedFormControl({value: '', disabled: true}),
      uspsCity: new UntypedFormControl(''),
      city: new UntypedFormControl('', [Validators.maxLength(45), Validators.required]),
      state: new UntypedFormControl('', [Validators.required]),
      stateId: new UntypedFormControl({ value: '', disabled: true }),
      zipCode: new UntypedFormControl('', [Validators.maxLength(6), CommonValidators.numeric, Validators.required]),
      countryId: new UntypedFormControl(''),
      phone: new UntypedFormControl('', [Validators.required]),
      phoneExt: new UntypedFormControl('', [Validators.maxLength(10)]),
      emailAddress: new UntypedFormControl('', [Validators.maxLength(45), CommonValidators.email, Validators.required]),
      alternatePhone: new UntypedFormControl('', [Validators.minLength(10), Validators.maxLength(20)]),
      otherContactInfo: new UntypedFormControl(''),
    });
    this.addressShipperForm = this.formBuilder.group({
      addressLine1: new UntypedFormControl(''),
      addressLine2: new UntypedFormControl('', [Validators.maxLength(100)]),
      searchString: new UntypedFormControl({value: '', disabled: true}),
      uspsCity: new UntypedFormControl(''),
      city: new UntypedFormControl({ value: '', disabled: true }, [Validators.maxLength(45)]),
      state: new UntypedFormControl({ value: '', disabled: true }),
      stateId: new UntypedFormControl({ value: '', disabled: true }),
      zipCode: new UntypedFormControl({ value: '', disabled: true }, [Validators.maxLength(6), CommonValidators.numeric]),
      countryId: new UntypedFormControl(''),
      phone: new UntypedFormControl(''),
      phoneExt: new UntypedFormControl('', [Validators.maxLength(10)]),
      emailAddress: new UntypedFormControl('', [Validators.maxLength(45), CommonValidators.email]),
      alternatePhone: new UntypedFormControl('', [Validators.minLength(10), Validators.maxLength(20)]),
      otherContactInfo: new UntypedFormControl(''),
    });
    this.addressBillToForm = this.formBuilder.group({
      addressLine1: new UntypedFormControl(''),
      addressLine2: new UntypedFormControl('', [Validators.maxLength(100)]),
      searchString: new UntypedFormControl({value: '', disabled: true}),
      uspsCity: new UntypedFormControl(''),
      city: new UntypedFormControl({ value: '', disabled: true }, [Validators.maxLength(45)]),
      state: new UntypedFormControl({ value: '', disabled: true }),
      stateId: new UntypedFormControl({ value: '', disabled: true }),
      zipCode: new UntypedFormControl({ value: '', disabled: true }, [Validators.maxLength(6), CommonValidators.numeric]),
      countryId: new UntypedFormControl(''),
      phone: new UntypedFormControl(''),
      phoneExt: new UntypedFormControl('', [Validators.maxLength(10)]),
      emailAddress: new UntypedFormControl('', [Validators.maxLength(45), CommonValidators.email]),
      alternatePhone: new UntypedFormControl('', [Validators.minLength(10), Validators.maxLength(20)]),
      otherContactInfo: new UntypedFormControl(''),
    });
    this.addressDestinationForm = this.formBuilder.group({
      addressLine1: new UntypedFormControl('', [Validators.required]),
      addressLine2: new UntypedFormControl('', [Validators.maxLength(100), Validators.required]),
      searchString: new UntypedFormControl({value: '', disabled: true}),
      uspsCity: new UntypedFormControl(''),
      city: new UntypedFormControl('', [Validators.maxLength(45), Validators.required]),
      state: new UntypedFormControl('', [Validators.required]),
      stateId: new UntypedFormControl({ value: '', disabled: true }),
      zipCode: new UntypedFormControl('', [Validators.maxLength(6), CommonValidators.numeric, Validators.required]),
      countryId: new UntypedFormControl(''),
      phone: new UntypedFormControl('', [Validators.required]),
      phoneExt: new UntypedFormControl('', [Validators.maxLength(10)]),
      emailAddress: new UntypedFormControl('', [Validators.maxLength(45), CommonValidators.email, Validators.required]),
      alternatePhone: new UntypedFormControl('', [Validators.minLength(10), Validators.maxLength(20)]),
      otherContactInfo: new UntypedFormControl(''),
    });
    this.addressConsigneeForm = this.formBuilder.group({
      addressLine1: new UntypedFormControl(''),
      addressLine2: new UntypedFormControl('', [Validators.maxLength(100)]),
      searchString: new UntypedFormControl({value: '', disabled: true}),
      uspsCity: new UntypedFormControl(''),
      city: new UntypedFormControl({ value: '', disabled: true }, [Validators.maxLength(45)]),
      state: new UntypedFormControl({ value: '', disabled: true }),
      stateId: new UntypedFormControl({ value: '', disabled: true }),
      zipCode: new UntypedFormControl({ value: '', disabled: true }, [Validators.maxLength(6), CommonValidators.numeric]),
      countryId: new UntypedFormControl(''),
      phone: new UntypedFormControl(''),
      phoneExt: new UntypedFormControl('', [Validators.maxLength(10)]),
      emailAddress: new UntypedFormControl('', [Validators.maxLength(45), CommonValidators.email]),
      alternatePhone: new UntypedFormControl('', [Validators.minLength(10), Validators.maxLength(20)]),
      otherContactInfo: new UntypedFormControl(''),
    });
    this.issuingOfficeForm = this.formBuilder.group({
      nameAddressId: new UntypedFormControl(''),
      shipmentsId: new UntypedFormControl(''),
      shipmentPrefAddressesId: new UntypedFormControl(''),
      firstName: new UntypedFormControl('', [Validators.maxLength(45)]),
      lastName: new UntypedFormControl('', [Validators.maxLength(45)]),
      middleInitial: new UntypedFormControl('', [Validators.maxLength(1)]),
      phone: new UntypedFormControl(''),
      issuingOfficerTitle: new UntypedFormControl('', [Validators.maxLength(45)]),
      issuingOfficeName: new UntypedFormControl('', [Validators.maxLength(45)])
    });
  }

  onSelectTab($event) {
    if ($event.heading === 'FRT View Rates') {
      this.activeTab = 'zero';
    } else if ($event.heading === 'FRT Template Download') {
      this.activeTab = 'one';
    } else if ($event.heading === 'FRT Upload Rate File') {
      this.activeTab = 'two';
    } else if ($event.heading === 'FRT Edit Rates') {
      this.activeTab = 'three';
    } else if ($event.heading === 'FRT Global Update') {
      this.activeTab = 'four';
    }
  }

  typeaheadAddressOnSelect($event) {
    const selectedAddress: ShipmentPrefAddress = $event.item;
    this.addressOriginForm.get('addressLine1').setValue(selectedAddress.addressLine1);
    this.addressOriginForm.get('addressLine2').setValue(selectedAddress.addressLine2);
    this.addressOriginForm.get('emailAddress').setValue(selectedAddress.emailAddress);
    this.addressOriginForm.get('firstName').setValue(selectedAddress.firstName);
    this.addressOriginForm.get('lastName').setValue(selectedAddress.lastName);
    this.addressOriginForm.get('middleInitial').setValue(selectedAddress.middleInitial);
    this.addressOriginForm.get('otherContactInfo').setValue(selectedAddress.otherContactInfo);
    this.addressOriginForm.get('phone').setValue(selectedAddress.phone);
    this.addressOriginForm.get('phoneExt').setValue(selectedAddress.phoneExt);
  }

  submit() {
    if (this.isSubmitDisable()) {
      [this.addressOriginForm, this.addressDestinationForm]
        .forEach(form => form.markAllAsTouched());
      return;
    }
    this.emitAddressData();
    this.bsModalRef.hide();
  }

  private emitAddressData(): void {
    this.emittedAddressData = true;
    this.emitData.emit({
      origin: this.addressOriginForm.getRawValue(),
      billTo: this.addressBillToForm.getRawValue(),
      shipper: this.addressShipperForm.getRawValue(),
      destination: this.addressDestinationForm.getRawValue(),
      consignee: this.addressConsigneeForm.getRawValue(),
      issuingOffice: this.issuingOfficeForm.getRawValue(),
      isRequiredComplete: this.addressOriginForm.valid && this.addressDestinationForm.valid
    });
  }

  isSubmitDisable() {
    this.isFormInvalidValue = [];
    if (!this.addressOriginForm.valid) {
      this.isFormInvalidValue.push('Origin')
    }
    if (!this.addressDestinationForm.valid) {
      this.isFormInvalidValue.push('Destination')
    }
    return this.isFormInvalidValue.length > 0;
  }

  cancel(): void {
    this.emittedAddressData = true;
    this.bsModalRef.hide();
  }
}
